import { AnimatedSection } from "@/components/common/AnimatedSection"
import { LegalPageLayout } from "@/components/common/LegalPageLayout"
import { LEGAL_LINKS } from "@/data/legal"
import { useSEO } from "@/hooks/useSEO"
import { createFileRoute } from "@tanstack/react-router"

export const Route = createFileRoute("/cookies")({
  component: CookiePolicyPage,
})

function CookiePolicyPage() {
  useSEO({
    title: "Cookie Policy",
    description: "Orchestrate's cookie policy – how we use cookies and similar technologies.",
    canonical: "https://orchestrate.global/cookie-policy",
  })

  return (
    <LegalPageLayout links={LEGAL_LINKS}>
      <div className="mx-auto max-w-3xl px-6 lg:px-8">
        <AnimatedSection delay={0.1}>
          <section className="border-border/50 border-b pb-16 lg:pb-20">
            <h1 className="font-display mb-6 text-4xl tracking-tight sm:text-5xl lg:text-6xl">
              Cookie Policy
            </h1>
            <p className="text-muted mb-10 text-xl">Last updated: February 27, 2026</p>
            <p className="text-ink/85 text-lg leading-relaxed">
              This Cookie Policy explains how Orchestrate Inc. ("we", "us", or "our") uses cookies
              and similar technologies to recognize you when you visit our websites at
              orchestrate.global (the "Website"). It describes what these technologies are and why
              we use them, as well as your rights to control our use of them.
            </p>
          </section>
        </AnimatedSection>

        <div className="space-y-16 py-12 lg:space-y-20 lg:py-16">
          <AnimatedSection delay={0.2}>
            <section>
              <h2 className="font-display mb-6 text-3xl tracking-tight">What are cookies?</h2>
              <p className="text-ink/80 mb-6 leading-relaxed">
                Cookies are small data files that are placed on your computer or mobile device when
                you visit a website. Cookies are widely used by website owners in order to make
                their websites work, or to work more efficiently, as well as to provide reporting
                information.
              </p>
              <p className="text-ink/80 leading-relaxed">
                Cookies set by the website owner (in this case, Orchestrate) are called "first party
                cookies". Cookies set by parties other than the website owner are called "third
                party cookies". Third party cookies enable third party features or functionality to
                be provided on or through the website (e.g. like analytics, advertising, videos).
              </p>
            </section>
          </AnimatedSection>

          <AnimatedSection delay={0.3}>
            <section>
              <h2 className="font-display mb-6 text-3xl tracking-tight">Why do we use cookies?</h2>
              <p className="text-ink/80 mb-6 leading-relaxed">
                We use first party and third party cookies for several reasons. Some cookies are
                required for technical reasons in order for our Website to operate, and we refer to
                these as "essential" or "strictly necessary" cookies. Other cookies also enable us
                to track and target the interests of our users to enhance the experience on our
                Website. Third parties serve cookies through our Website for advertising, analytics
                and other purposes.
              </p>
            </section>
          </AnimatedSection>

          <AnimatedSection delay={0.4}>
            <section>
              <h2 className="font-display mb-6 text-3xl tracking-tight">Types of cookies we use</h2>
              <div className="space-y-8">
                <div>
                  <h3 className="mb-4 text-2xl font-semibold tracking-tight">Essential Cookies</h3>
                  <p className="text-ink/80 mb-4 leading-relaxed">
                    These cookies are necessary for the website to function and cannot be switched
                    off in our systems. They are usually only set in response to actions made by you
                    which amount to a request for services, such as setting your privacy
                    preferences, logging in or filling in forms.
                  </p>
                </div>

                <div>
                  <h3 className="mb-4 text-2xl font-semibold tracking-tight">
                    Performance / Analytics Cookies
                  </h3>
                  <p className="text-ink/80 mb-4 leading-relaxed">
                    These cookies allow us to count visits and traffic sources so we can measure and
                    improve the performance of our site. They help us to know which pages are the
                    most and least popular and see how visitors move around the site.
                  </p>
                </div>

                <div>
                  <h3 className="mb-4 text-2xl font-semibold tracking-tight">Functional Cookies</h3>
                  <p className="text-ink/80 mb-4 leading-relaxed">
                    These cookies enable the website to provide enhanced functionality and
                    personalization. They may be set by us or by third party providers whose
                    services we have added to our pages.
                  </p>
                </div>

                <div>
                  <h3 className="mb-4 text-2xl font-semibold tracking-tight">
                    Targeting / Advertising Cookies
                  </h3>
                  <p className="text-ink/80 leading-relaxed">
                    These cookies may be set through our site by our advertising partners. They may
                    be used by those companies to build a profile of your interests and show you
                    relevant adverts on other sites.
                  </p>
                </div>
              </div>
            </section>
          </AnimatedSection>

          <AnimatedSection delay={0.5}>
            <section>
              <h2 className="font-display mb-6 text-3xl tracking-tight">
                List of cookies used on our Website
              </h2>
              <p className="text-ink/80 mb-6 leading-relaxed">
                The table below lists the specific cookies we use on our Website, categorized by
                purpose.
              </p>

              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="border-border bg-surface/50 border-b">
                      <th className="text-ink px-6 py-4 text-left font-semibold tracking-wider uppercase">
                        Cookie Name
                      </th>
                      <th className="text-ink px-6 py-4 text-left font-semibold tracking-wider uppercase">
                        Provider
                      </th>
                      <th className="text-ink px-6 py-4 text-left font-semibold tracking-wider uppercase">
                        Purpose
                      </th>
                      <th className="text-ink px-6 py-4 text-left font-semibold tracking-wider uppercase">
                        Category
                      </th>
                      <th className="text-ink px-6 py-4 text-left font-semibold tracking-wider uppercase">
                        Duration
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-border divide-y">
                    <tr>
                      <td className="text-ink/80 px-6 py-4">session_id</td>
                      <td className="text-ink/80 px-6 py-4">Orchestrate</td>
                      <td className="text-ink/80 px-6 py-4">
                        Maintains your session and authentication state
                      </td>
                      <td className="text-ink/80 px-6 py-4 font-medium">Essential</td>
                      <td className="text-ink/80 px-6 py-4">Session</td>
                    </tr>
                    <tr className="bg-surface/30">
                      <td className="text-ink/80 px-6 py-4">_ga</td>
                      <td className="text-ink/80 px-6 py-4">Google Analytics</td>
                      <td className="text-ink/80 px-6 py-4">Distinguishes unique users</td>
                      <td className="text-ink/80 px-6 py-4 font-medium">Analytics</td>
                      <td className="text-ink/80 px-6 py-4">2 years</td>
                    </tr>
                    <tr>
                      <td className="text-ink/80 px-6 py-4">_gid</td>
                      <td className="text-ink/80 px-6 py-4">Google Analytics</td>
                      <td className="text-ink/80 px-6 py-4">Tracks user sessions</td>
                      <td className="text-ink/80 px-6 py-4 font-medium">Analytics</td>
                      <td className="text-ink/80 px-6 py-4">24 hours</td>
                    </tr>
                    <tr className="bg-surface/30">
                      <td className="text-ink/80 px-6 py-4">_fbp</td>
                      <td className="text-ink/80 px-6 py-4">Meta (Facebook Pixel)</td>
                      <td className="text-ink/80 px-6 py-4">Advertising and retargeting</td>
                      <td className="text-ink/80 px-6 py-4 font-medium">Marketing</td>
                      <td className="text-ink/80 px-6 py-4">3 months</td>
                    </tr>
                    <tr>
                      <td className="text-ink/80 px-6 py-4">pref_theme</td>
                      <td className="text-ink/80 px-6 py-4">Orchestrate</td>
                      <td className="text-ink/80 px-6 py-4">
                        Remembers your theme preference (light/dark)
                      </td>
                      <td className="text-ink/80 px-6 py-4 font-medium">Functional</td>
                      <td className="text-ink/80 px-6 py-4">1 year</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-ink/80 mt-6 text-sm leading-relaxed italic">
                Note: This table includes examples of common cookies. The actual cookies used may
                vary. For the most up-to-date list, please refer to your browser's cookie settings
                or our consent management tool.
              </p>
            </section>
          </AnimatedSection>

          <AnimatedSection delay={0.6}>
            <section>
              <h2 className="font-display mb-6 text-3xl tracking-tight">How to manage cookies</h2>
              <p className="text-ink/80 mb-6 leading-relaxed">
                You can set or amend your web browser controls to accept or refuse cookies. If you
                choose to reject cookies, you may still use our Website though your access to some
                functionality and areas of our Website may be restricted.
              </p>
              <p className="text-ink/80 leading-relaxed">
                Most web browsers allow some control of most cookies through the browser settings.
                To find out more about cookies, including how to see what cookies have been set and
                how to manage and delete them, visit www.allaboutcookies.org or
                www.aboutcookies.org.
              </p>
            </section>
          </AnimatedSection>

          <AnimatedSection delay={0.7}>
            <section>
              <h2 className="font-display mb-6 text-3xl tracking-tight">
                Changes to this Cookie Policy
              </h2>
              <p className="text-ink/80 leading-relaxed">
                We may update this Cookie Policy from time to time in order to reflect, for example,
                changes to the cookies we use or for other operational, legal or regulatory reasons.
                Please therefore re-visit this Cookie Policy regularly to stay informed about our
                use of cookies and related technologies.
              </p>
            </section>
          </AnimatedSection>

          <AnimatedSection delay={0.8}>
            <section>
              <h2 className="font-display mb-6 text-3xl tracking-tight">Contact</h2>
              <p className="text-ink/80 mb-6 leading-relaxed">
                If you have any questions about our use of cookies or this Cookie Policy, please
                contact us at:
              </p>
              <div className="bg-surface/50 border-border/50 space-y-3 rounded-xl border p-6">
                <p className="text-ink">
                  <strong>Email:</strong> legal@orchestrate.global
                </p>
                <p className="text-ink">
                  <strong>Address:</strong> Orchestrate Inc., 123 Financial District, San Francisco,
                  CA 94105
                </p>
              </div>
            </section>
          </AnimatedSection>
        </div>
      </div>
    </LegalPageLayout>
  )
}
